/**
 * HUD-Teil: Abschlussbildschirm und Fähigkeitswahl.
 *
 * Die **Fähigkeitswahl** ist der Moment, in dem sich der Spieler mit seiner
 * Figur befasst (Ueberarbeitung B10): zwei Karten, ein Tipp, weiter. Kein Menü,
 * kein Baum, keine Punkteverteilung — nur eine Entscheidung, die etwas ändert.
 */

import type { Ereignis, Schnappschuss } from '@pg/protocol';
import { KRUEMEL_ZEILEN, waehleZeile } from '../zeilen.js';
import { el, type HudTeil, type HudUmgebung } from './typen.js';

let ertragGold = 0;
let ertragStuecke = 0;

export function setzeErtrag(gold: number, stuecke: number): void {
  ertragGold = gold;
  ertragStuecke = stuecke;
}

export const abschluss: HudTeil = {
  name: 'abschluss',

  bauen(u: HudUmgebung) {
    const knopf = el<HTMLButtonElement>('#nochmal');
    if (knopf.dataset['verdrahtet']) {
      el<HTMLDivElement>('#abschluss').hidden = true;
      return;
    }
    knopf.dataset['verdrahtet'] = '1';
    // 'click' auf dem Knopf reicht jetzt: Die Steuerung horcht nur noch auf
    // der Spielflaeche und fischt den Zeiger nicht mehr weg (Ueberarbeitung A1).
    knopf.addEventListener('click', () => u.neuerAbstieg());
    el<HTMLDivElement>('#abschluss').hidden = true;
  },

  ereignis(_u, e: Ereignis, s: Schnappschuss) {
    if (e.art === 'abstieg-geschafft') zeige(true, e.dauer, s);
    if (e.art === 'spieler-gestorben') zeige(false, s.zeit, s);
  },
};

function zeige(geschafft: boolean, dauer: number, s: Schnappschuss): void {
  const kasten = el<HTMLDivElement>('#abschluss');
  (kasten.querySelector('h2') as HTMLElement).textContent = geschafft
    ? 'Abstieg abgeschlossen'
    : 'Nicht zurückgekommen';

  const minuten = Math.floor(dauer / 60);
  const sekunden = Math.round(dauer % 60);
  (kasten.querySelector('dl') as HTMLElement).innerHTML =
    `<dt>Dauer</dt><dd>${minuten}:${String(sekunden).padStart(2, '0')}</dd>` +
    `<dt>Beute</dt><dd>${ertragStuecke}</dd>` +
    `<dt>Gold</dt><dd>${ertragGold}</dd>` +
    `<dt>Stufe</dt><dd>${s.spieler.stufe}</dd>`;
  (kasten.querySelector('.ledger') as HTMLElement).textContent = waehleZeile(
    KRUEMEL_ZEILEN[geschafft ? 'geschafft' : 'gescheitert'],
    Math.round(dauer * 1000) + ertragStuecke,
  );

  const knopf = el<HTMLButtonElement>('#nochmal');
  // Klar sagen, was als Naechstes kommt — vorher war unklar, ob der Knopf
  // ueberhaupt etwas tut.
  knopf.textContent = geschafft
    ? `Tiefer hinab — Tiefe ${s.abstieg.tiefe + 1}`
    : `Noch einmal — Tiefe ${s.abstieg.tiefe}`;
  kasten.hidden = false;
}

export const wahl: HudTeil = {
  name: 'wahl',

  aktualisieren(u: HudUmgebung, s: Schnappschuss) {
    const kasten = el<HTMLDivElement>('#wahl');
    const offen = s.spieler.offeneWahl;

    if (!offen || offen.length === 0) {
      kasten.hidden = true;
      kasten.dataset['gezeigt'] = '';
      return;
    }

    const schluessel = offen.map((a) => a.id).join('|');
    if (kasten.dataset['gezeigt'] === schluessel) return;
    kasten.dataset['gezeigt'] = schluessel;

    const liste = kasten.querySelector('.karten') as HTMLElement;
    liste.innerHTML = '';
    for (const a of offen) {
      const karte = document.createElement('button');
      karte.type = 'button';
      karte.className = 'angebot';
      karte.innerHTML =
        `<h3>${a.name}</h3>` +
        `<p>${a.beschreibung}</p>` +
        (a.bewegtSpieler
          ? '<span class="warn">Versetzt dich — feuert nur, wenn du es willst</span>'
          : '');
      karte.addEventListener('click', () => {
        u.steuerung.waehleFaehigkeit(a.id);
        kasten.hidden = true;
      });
      liste.appendChild(karte);
    }
    kasten.hidden = false;
  },
};
